import React from 'react';

const MachiningPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-primary-500 text-secondary-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl md:text-5xl font-bold mb-4">
              Machining Services
            </h1>
            <p className="text-lg md:text-2xl text-secondary-100 max-w-3xl mx-auto">
              Precision machining solutions with collaboration and backing of
              Sohar University
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Image Section - Left side on desktop, top on mobile */}
          <div className="lg:sticky lg:top-36 order-1 lg:order-1 animate-fade-in-left">
            <div className="rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <img
                src="/machining.png"
                alt="Precision Machining"
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* Content Section - Right side on desktop, bottom on mobile */}
          <div className="space-y-6 md:space-y-8 order-2 lg:order-2 animate-fade-in-right">
            {/* University Collaboration */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: '0.1s' }}
            >
              <div className="bg-primary-50 p-6 rounded-lg shadow-md border-l-4 border-primary-500">
                <p className="text-sm md:text-base text-gray-700">
                  Specialize in providing comprehensive machining solutions,
                  leveraging our extensive experience with a wide variety of
                  industrial components
                </p>
              </div>
            </div>

            {/* General Machining Operations */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: '0.2s' }}
            >
              <h3 className="text-xl md:text-2xl font-bold text-primary-500 mb-4">
                General Machining Operations
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  'Turning',
                  'Threading',
                  'Grooving',
                  'Milling',
                  'Boring',
                  'Slotting',
                  'Shaping',
                  'Parting',
                  'Reaming',
                  'Grinding',
                ].map((operation, index) => (
                  <div
                    key={index}
                    className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 animate-fade-in hover:shadow-md transition-shadow duration-200 text-center"
                    style={{ animationDelay: `${0.25 + index * 0.05}s` }}
                  >
                    <span className="text-sm md:text-base font-medium text-gray-800">
                      {operation}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Specialized Machining */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: '0.3s' }}
            >
              <h3 className="text-xl md:text-2xl font-bold text-primary-500 mb-4">
                Specialized Machining Components
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  'Flange Overlay Machining',
                  'Various Flanges',
                  'Flange Center Drilling & Tapping',
                  'Nipple',
                  'Coupling',
                  'Roller Shaft',
                  'Taper Plug',
                  'Bracket Photo Cell',
                  'Rack & Pinion',
                  'Sprocket for Bar Counter',
                  'Roller Assembly',
                  'Anchor Bolts',
                  'Vee Blocks',
                  'Edge Clamps',
                  'Support Blocks',
                  'Vise Brake',
                ].map((component, index) => (
                  <div
                    key={index}
                    className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 animate-fade-in hover:shadow-md transition-shadow duration-200 flex items-center space-x-3"
                    style={{ animationDelay: `${0.35 + index * 0.03}s` }}
                  >
                    <div className="flex-shrink-0 w-2 h-2 bg-primary-500 rounded-full"></div>
                    <span className="text-sm md:text-base font-medium text-gray-800">
                      {component}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Machining Capabilities */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: '0.4s' }}
            >
              <h3 className="text-xl md:text-2xl font-bold text-primary-500 mb-4">
                Our Machining Capabilities
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h4 className="font-semibold text-gray-800 mb-3">
                    Precision Operations
                  </h4>
                  <ul className="space-y-2 text-sm md:text-base text-gray-700">
                    <li className="flex items-start space-x-2">
                      <span className="text-primary-500">•</span>
                      <span>High-precision turning and milling</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-primary-500">•</span>
                      <span>Complex threading operations</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-primary-500">•</span>
                      <span>Surface grinding and finishing</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h4 className="font-semibold text-gray-800 mb-3">
                    Custom Solutions
                  </h4>
                  <ul className="space-y-2 text-sm md:text-base text-gray-700">
                    <li className="flex items-start space-x-2">
                      <span className="text-primary-500">•</span>
                      <span>Prototype development</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-primary-500">•</span>
                      <span>Small to medium batch production</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-primary-500">•</span>
                      <span>Repair and refurbishment</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Quality & Standards */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: '0.5s' }}
            >
              <h3 className="text-xl md:text-2xl font-bold text-primary-500 mb-4">
                Quality & Standards
              </h3>
              <div className="flex flex-wrap gap-3">
                {[
                  'Precision Machining',
                  'Quality Control',
                  'Academic Backing',
                  'Industry Standards',
                  'Custom Solutions',
                  'Prototype Development',
                ].map((standard, index) => (
                  <span
                    key={index}
                    className="bg-primary-100 text-primary-500 px-4 py-2 rounded-full text-sm font-medium animate-fade-in hover:bg-primary-200 transition-colors duration-200"
                    style={{ animationDelay: `${0.55 + index * 0.1}s` }}
                  >
                    {standard}
                  </span>
                ))}
              </div>
            </div>

            {/* Equipment & Technology */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: '0.6s' }}
            >
              <h3 className="text-xl md:text-2xl font-bold text-primary-500 mb-4">
                Equipment & Technology
              </h3>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">
                      Machine Tools
                    </h4>
                    <ul className="text-sm md:text-base text-gray-700 space-y-1">
                      <li>• CNC Turning Centers</li>
                      <li>• Milling Machines</li>
                      <li>• Grinding Equipment</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">
                      Capabilities
                    </h4>
                    <ul className="text-sm md:text-base text-gray-700 space-y-1">
                      <li>• Complex Geometries</li>
                      <li>• Tight Tolerances</li>
                      <li>• Various Materials</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="bg-primary-500 text-secondary-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Need Precision Machining Services?
          </h2>
          <p className="text-lg md:text-xl text-secondary-100 mb-8 max-w-2xl mx-auto">
            Backed by Sohar University's expertise, we deliver high-quality
            machining solutions for your industrial needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="bg-secondary-500 text-primary-500 px-8 py-3 rounded-lg font-semibold hover:bg-secondary-100 transition-colors cursor-pointer"
            >
              Request Quote
            </a>
            <a
              href="/services"
              className="border-2 border-secondary-500 text-secondary-500 px-8 py-3 rounded-lg font-semibold hover:bg-secondary-500 hover:text-primary-500 transition-colors cursor-pointer"
            >
              View All Services
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MachiningPage;
